/**
 *  @class
 *  @function CollectionMenuExpand
 */
if (!customElements.get('collection-menu-expand')) {
  class CollectionMenuExpand extends HTMLElement {
    constructor() {
      super();
      this.onToggle = this.onToggle.bind(this);
      this.update = this.update.bind(this);
      this.mobileQuery = window.matchMedia('(max-width: 47.9375rem)');
      this.isAnimating = false;
    }

    connectedCallback() {
      this.menu = this.querySelector('[data-collection-menu-expand]');
      this.moreItem = this.querySelector('[data-collection-menu-expand-more]');
      this.toggle = this.querySelector('[data-collection-menu-expand-toggle]');
      this.countEl = this.querySelector('[data-collection-menu-expand-count]');

      if (!this.menu || !this.moreItem || !this.toggle || !this.countEl) return;

      this.toggle.addEventListener('click', this.onToggle);
      this.mobileQuery.addEventListener('change', this.update);
      this.resizeObserver = new ResizeObserver(this.update);
      this.resizeObserver.observe(this.menu);
      requestAnimationFrame(this.update);
    }

    disconnectedCallback() {
      this.toggle?.removeEventListener('click', this.onToggle);
      this.mobileQuery?.removeEventListener('change', this.update);
      this.resizeObserver?.disconnect();
    }

    get items() {
      return Array.from(this.menu.querySelectorAll('li:not([data-collection-menu-expand-more])'));
    }

    getMaxLines() {
      return this.mobileQuery.matches ? 2 : 1;
    }

    getUniqueRowTops(elements) {
      const tops = [];

      elements.forEach((element) => {
        const top = element.offsetTop;
        if (!tops.some((existing) => Math.abs(existing - top) < 2)) {
          tops.push(top);
        }
      });

      return tops.sort((a, b) => a - b);
    }

    prefersReducedMotion() {
      return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    }

    onToggle() {
      if (this.hasAttribute('data-always-expanded') || this.classList.contains('is-expanded') || this.isAnimating) {
        return;
      }

      this.expand();
    }

    expand() {
      const startHeight = this.menu.offsetHeight;

      this.items.forEach((item) => {
        item.hidden = false;
      });
      this.moreItem.hidden = true;
      this.classList.add('is-expanded', 'is-ready');
      this.toggle.setAttribute('aria-expanded', 'true');

      if (this.prefersReducedMotion()) {
        return;
      }

      const endHeight = this.menu.scrollHeight;

      if (endHeight <= startHeight) {
        return;
      }

      this.isAnimating = true;
      this.menu.style.overflow = 'hidden';
      this.menu.style.maxHeight = `${startHeight}px`;

      requestAnimationFrame(() => {
        this.menu.classList.add('is-animating');
        this.menu.style.maxHeight = `${endHeight}px`;
      });

      const onEnd = (event) => {
        if (event.target !== this.menu || event.propertyName !== 'max-height') return;

        this.menu.removeEventListener('transitionend', onEnd);
        this.menu.classList.remove('is-animating');
        this.menu.style.maxHeight = '';
        this.menu.style.overflow = '';
        this.isAnimating = false;
      };

      this.menu.addEventListener('transitionend', onEnd);
    }

    update() {
      if (this.isAnimating) return;

      const items = this.items;

      if (!items.length || this.menu.clientWidth === 0) {
        return;
      }

      items.forEach((item) => {
        item.hidden = false;
      });

      if (this.hasAttribute('data-always-expanded') || this.classList.contains('is-expanded')) {
        this.moreItem.hidden = true;
        this.classList.add('is-expanded', 'is-ready');
        this.toggle.setAttribute('aria-expanded', 'true');
        return;
      }

      this.moreItem.hidden = true;

      const maxLines = this.getMaxLines();
      const allRowTops = this.getUniqueRowTops(items);

      if (allRowTops.length <= maxLines) {
        this.classList.add('is-ready');
        this.toggle.setAttribute('aria-expanded', 'false');
        return;
      }

      this.moreItem.hidden = false;
      this.countEl.textContent = `+${items.length}`;

      let visibleCount = items.length;

      while (visibleCount > 0) {
        items.forEach((item, index) => {
          item.hidden = index >= visibleCount;
        });

        const visibleItems = items.slice(0, visibleCount);
        const rowTops = this.getUniqueRowTops([...visibleItems, this.moreItem]);

        if (rowTops.length <= maxLines) {
          break;
        }

        visibleCount -= 1;
      }

      const hiddenCount = items.length - visibleCount;

      if (hiddenCount <= 0) {
        items.forEach((item) => {
          item.hidden = false;
        });
        this.moreItem.hidden = true;
      } else {
        this.countEl.textContent = `+${hiddenCount}`;
        this.moreItem.hidden = false;
      }

      this.classList.add('is-ready');
      this.toggle.setAttribute('aria-expanded', 'false');
    }
  }

  customElements.define('collection-menu-expand', CollectionMenuExpand);
}
